/** Pure labels for nudge prompts, replies and message kinds (Stage 5). */
import type { NudgeSummary, Prompt, PromptKind } from '@/features/pipeline/types';
import { NUDGE_REPLIES } from '@/features/settings/types';

export function promptKindLabel(kind: PromptKind): string {
  return kind === 'notes_question' ? 'notes' : 'status';
}

/** The worker's sentinel for a status prompt closed because another recipient answered first (Stage 5 QA carry-over a). */
export const SUPERSEDED_ANSWER = 'superseded';

/** `"1"` → `"Qualified – need to schedule interview"`; free text passes through; `NO` → `"No notes"`; `superseded` → who answered instead. */
export function promptAnswerLabel(prompt: Pick<Prompt, 'kind' | 'answer'>): string | null {
  const raw = prompt.answer?.trim();
  if (!raw) return null;
  if (raw === SUPERSEDED_ANSWER) return 'Answered by another recipient';
  if (prompt.kind === 'status_question') {
    const option = NUDGE_REPLIES.find((r) => r.digit === raw);
    return option ? `${option.digit} — ${option.label}` : raw;
  }
  return /^n(o)?$/i.test(raw) ? 'No notes' : raw;
}

export type PromptState = 'answered' | 'expired' | 'open';

export function promptState(prompt: Pick<Prompt, 'answered_at' | 'expires_at'>, now: Date = new Date()): PromptState {
  if (prompt.answered_at) return 'answered';
  if (prompt.expires_at && new Date(prompt.expires_at).getTime() <= now.getTime()) return 'expired';
  return 'open';
}

/** Short label for the queue `kind` of a nudge-related text, or `null` for every other kind. */
export function nudgeMessageKindLabel(kind: string | null | undefined): string | null {
  switch (kind) {
    case 'nudge_status':
      return 'Nudge';
    case 'nudge_notes':
      return 'Notes question';
    case 'nudge_ack':
      return 'Confirmation';
    default:
      return null;
  }
}

/** The header line: `awaiting reply (status)` / `awaiting reply (notes)` / nothing open. */
export function nudgeStatusLabel(nudge: NudgeSummary | null | undefined): { sentAt: string; awaiting: string | null } | null {
  if (!nudge?.last_sent_at) return null;
  return {
    sentAt: nudge.last_sent_at,
    awaiting: nudge.open_prompt_kind ? `awaiting reply (${promptKindLabel(nudge.open_prompt_kind)})` : null,
  };
}
